/**
 * Wandelt eine CSS-Deklarationsfolge in ein React-Stilobjekt.
 *
 * Der HTML-Prototyp schreibt seine Stile als Zeichenfolgen in das
 * Markup. Damit der Nachbau nicht bei jeder Uebernahme umformuliert werden
 * muss — und damit ein Abgleich mit der Vorlage Zeile fuer Zeile moeglich
 * bleibt — werden diese Zeichenfolgen hier woertlich uebernommen und erst zur
 * Laufzeit uebersetzt.
 *
 * Farbwerte stehen ausschliesslich als Token (`var(--…)`) in den Vorlagen;
 * dieser Helfer aendert daran nichts.
 */

import type { CSSProperties } from 'react';

/** Eigenschaften, die React als Zahl missversteht, wenn man sie nicht schuetzt. */
const EINHEITENFREI = new Set([
  'z-index', 'opacity', 'flex-grow', 'flex-shrink', 'order',
  'line-height', 'font-weight', 'zoom', 'tab-size',
]);

const zwischenspeicher = new Map<string, CSSProperties>();

function nachCamel(name: string): string {
  if (name.startsWith('--')) return name;            // eigene Eigenschaft: unveraendert
  if (name.startsWith('-ms-')) name = name.slice(1); // React erwartet msFoo, nicht MsFoo
  return name.replace(/-([a-z])/g, (_, z: string) => z.toUpperCase());
}

/**
 * `stil("display:flex;gap:8px")` → `{ display: 'flex', gap: '8px' }`
 *
 * Semikolon innerhalb von Klammern — etwa in `url(...)` oder `rgb(0 0 0/.14)` —
 * kommt in den Vorlagen nicht vor; der Zerleger bleibt deshalb bewusst schlicht
 * und trennt an jedem Semikolon ausserhalb von Klammern.
 */
export function stil(css: string): CSSProperties {
  const bekannt = zwischenspeicher.get(css);
  if (bekannt) return bekannt;

  const ergebnis: Record<string, string | number> = {};
  let tiefe = 0;
  let start = 0;

  const teile: string[] = [];
  for (let i = 0; i < css.length; i++) {
    const z = css[i];
    if (z === '(') tiefe++;
    else if (z === ')') tiefe--;
    else if (z === ';' && tiefe === 0) { teile.push(css.slice(start, i)); start = i + 1; }
  }
  teile.push(css.slice(start));

  for (const teil of teile) {
    const trennung = teil.indexOf(':');
    if (trennung < 0) continue;
    const name = teil.slice(0, trennung).trim();
    const wert = teil.slice(trennung + 1).trim();
    if (!name || !wert) continue;
    // Einheitenfreie Zahlen als Zahl, alles andere als Zeichenfolge —
    // React haengt sonst ungefragt "px" an.
    ergebnis[nachCamel(name)] =
      EINHEITENFREI.has(name) && wert !== '' && !Number.isNaN(Number(wert))
        ? Number(wert)
        : wert;
  }

  const fertig = ergebnis as CSSProperties;
  zwischenspeicher.set(css, fertig);
  return fertig;
}

/** Mehrere Stilteile zusammenfuehren; spaetere gewinnen. */
export function stile(...teile: (string | false | null | undefined)[]): CSSProperties {
  return Object.assign({}, ...teile.filter(Boolean).map((t) => stil(t as string)));
}
